import React, { useEffect, useState } from 'react'
import { Form, Input, Message, Modal, Tab } from 'semantic-ui-react'
import { X } from 'phosphor-react'
import styled from 'styled-components'
import {
    AppPrimaryButton,
    AppSecondaryButton,
    SignatureFormField,
} from 'elements'
import { FlexRowBetween } from 'components'
import { SIGNATURE_TYPE, buildApprovalPayload } from './report-approval-utils'
import { UploadDropZone } from './report-approval-styles'

const ApproveModal = styled(Modal)`
    .content {
        padding: 1.5rem !important;
        max-height: 80vh !important;
        overflow-y: auto !important;
    }

    .signature-input {
        margin-bottom: 16px;

        label {
            display: block;
            margin-bottom: 8px;
            font-weight: 600;
        }
    }

    .signature-pad {
        border: 1px solid #ddd;
        border-radius: 4px;
        margin: 12px 0 0;
        padding: 16px;
        background: #fff;
    }

    .signature-preview {
        margin-top: 8px;
        padding: 12px;
        border: 1px solid #eee;
        border-radius: 4px;
        min-height: 90px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-family: 'Allura', 'Segoe Script', cursive;
        font-size: 36px;
        color: #111827;
        background: #fafafa;
        text-align: center;
        word-break: break-word;
    }

    .notes-field {
        margin-bottom: 16px;

        label {
            display: block;
            margin-bottom: 8px;
            font-weight: 600;
        }
    }

    .signature-tabs {
        margin-top: 8px;
    }
`

const readFileAsDataUrl = file =>
    new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result)
        reader.onerror = reject
        reader.readAsDataURL(file)
    })

export const ApproveReportModal = ({
    open,
    onClose,
    onSubmit,
    submitting = false,
    error = '',
}) => {
    const [activeTab, setActiveTab] = useState(SIGNATURE_TYPE.Draw)
    const [notes, setNotes] = useState('')
    const [drawnSignature, setDrawnSignature] = useState('')
    const [uploadedSignature, setUploadedSignature] = useState('')
    const [typedName, setTypedName] = useState('')
    const [validationError, setValidationError] = useState('')

    useEffect(() => {
        if (!open) {
            setNotes('')
            setDrawnSignature('')
            setUploadedSignature('')
            setTypedName('')
            setValidationError('')
            setActiveTab(SIGNATURE_TYPE.Draw)
        }
    }, [open])

    const handleUpload = async event => {
        const file = event.target.files?.[0]
        if (!file) return
        if (!file.type.startsWith('image/')) {
            setValidationError('Please upload an image file.')
            return
        }
        const dataUrl = await readFileAsDataUrl(file)
        setUploadedSignature(String(dataUrl))
        setValidationError('')
    }

    const validate = () => {
        if (!notes.trim()) {
            setValidationError('Notes are required.')
            return false
        }

        if (activeTab === SIGNATURE_TYPE.Draw && !drawnSignature) {
            setValidationError(
                'Please draw your signature and click Save before approving.',
            )
            return false
        }

        if (activeTab === SIGNATURE_TYPE.Upload && !uploadedSignature) {
            setValidationError('Please upload a signature image.')
            return false
        }

        if (activeTab === SIGNATURE_TYPE.Typed && !typedName.trim()) {
            setValidationError('Please type your name.')
            return false
        }

        setValidationError('')
        return true
    }

    const handleSubmit = () => {
        if (!validate()) return

        const payload = buildApprovalPayload({
            notes,
            signatureType: activeTab,
            signatureBase64:
                activeTab === SIGNATURE_TYPE.Draw
                    ? drawnSignature
                    : uploadedSignature,
            signatureText: typedName,
            notifyAuditor: false,
        })

        onSubmit(payload)
    }

    const panes = [
        {
            menuItem: 'Draw',
            render: () => (
                <Tab.Pane attached={false}>
                    <SignatureFormField
                        label="Draw your signature"
                        value={drawnSignature}
                        onChange={setDrawnSignature}
                    />
                </Tab.Pane>
            ),
        },
        {
            menuItem: 'Upload',
            render: () => (
                <Tab.Pane attached={false}>
                    <UploadDropZone>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={handleUpload}
                        />
                        {uploadedSignature ? (
                            <img
                                src={uploadedSignature}
                                alt="Uploaded signature"
                            />
                        ) : (
                            <>
                                <strong>Click to upload</strong>
                                <span style={{ fontSize: 12, marginTop: 6 }}>
                                    PNG or JPG signature image
                                </span>
                            </>
                        )}
                    </UploadDropZone>
                </Tab.Pane>
            ),
        },
        {
            menuItem: 'Type Name',
            render: () => (
                <Tab.Pane attached={false}>
                    <Form.Field className="signature-input">
                        <label>Type your full name</label>
                        <Input
                            fluid
                            placeholder="Enter your name..."
                            value={typedName}
                            onChange={(_, { value }) => setTypedName(value)}
                        />
                    </Form.Field>
                    <div className="signature-pad">
                        <label className="font-extrabold">Your Signature</label>
                        <div className="signature-preview">
                            {typedName.trim() || 'Signature preview'}
                        </div>
                    </div>
                </Tab.Pane>
            ),
        },
    ]

    return (
        <ApproveModal
            open={open}
            onClose={onClose}
            size="small"
            closeOnDimmerClick={false}
        >
            <Modal.Header>
                <FlexRowBetween>
                    <span>Approve Report</span>
                    <X className="pointer" size={24} onClick={onClose} />
                </FlexRowBetween>
            </Modal.Header>
            <Modal.Content>
                <Message warning size="small">
                    Notify auditor is not available yet — email notifications
                    are deferred to a future release.
                </Message>

                {(validationError || error) && (
                    <Message negative size="small">
                        {validationError || error}
                    </Message>
                )}

                <Form>
                    <Form.Field className="notes-field" required>
                        <label>Notes</label>
                        <Form.TextArea
                            value={notes}
                            onChange={(_, { value }) => setNotes(value)}
                            placeholder="Approval notes (required)"
                            rows={3}
                        />
                    </Form.Field>
                </Form>

                <div className="signature-tabs">
                    <Tab
                        menu={{ secondary: true, pointing: true }}
                        panes={panes}
                        activeIndex={activeTab - 1}
                        onTabChange={(_, { activeIndex }) =>
                            setActiveTab(activeIndex + 1)
                        }
                    />
                </div>
            </Modal.Content>
            <Modal.Actions>
                <AppSecondaryButton type="button" onClick={onClose}>
                    Cancel
                </AppSecondaryButton>
                <AppPrimaryButton
                    type="button"
                    onClick={handleSubmit}
                    disabled={submitting}
                >
                    {submitting ? 'Approving…' : 'Approve report'}
                </AppPrimaryButton>
            </Modal.Actions>
        </ApproveModal>
    )
}
